// src/theme/theme.js
import { createTheme } from "@mui/material/styles";
import { THEME_DEFAULTS } from "../context/themeConfig";

const fontFamily = [
  "system-ui",
  "-apple-system",
  "Segoe UI",
  "Roboto",
  "Noto Sans JP",
  "Hiragino Sans",
  "Meiragino",
  "Meiryo",
  "sans-serif",
].join(",");

// config = Firestoreから取得した値（なければデフォルト）
function buildBase(c) {
  return {
    shape: { borderRadius: c.borderRadius },
    typography: {
      fontFamily,
      h1: { fontWeight: 900, letterSpacing: -0.6 },
      h2: { fontWeight: 900, letterSpacing: -0.4 },
      h3: { fontWeight: 900, letterSpacing: -0.3 },
      h4: { fontWeight: 900, letterSpacing: -0.2 },
      h5: { fontWeight: 900, letterSpacing: -0.1 },
      h6: { fontWeight: 900 },
      button: { fontWeight: 900 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: { WebkitFontSmoothing: "antialiased", MozOsxFontSmoothing: "grayscale" },
          body: {
            backgroundImage:
              "radial-gradient(900px 500px at 15% 0%, rgba(46,78,63,0.06), transparent 60%)," +
              "radial-gradient(800px 420px at 85% 0%, rgba(124,45,18,0.05), transparent 55%)," +
              "radial-gradient(900px 520px at 50% 100%, rgba(51,65,85,0.04), transparent 60%)",
            backgroundAttachment: "fixed",
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: { boxShadow: "none" },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: { minHeight: 64 },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 900,
            borderRadius: c.buttonRadius,
            paddingLeft: 14,
            paddingRight: 14,
          },
          contained: { boxShadow: "none" },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: c.cardRadius,
            boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
            border: "1px solid rgba(0,0,0,0.05)",
            overflow: "hidden",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          rounded: { borderRadius: c.paperRadius },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: c.dialogRadius,
            boxShadow: "0 24px 64px rgba(0,0,0,0.18), 0 8px 24px rgba(0,0,0,0.10)",
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: c.menuRadius,
            border: "1px solid rgba(0,0,0,0.06)",
            boxShadow: "0 18px 50px rgba(0,0,0,0.12)",
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: c.menuItemRadius,
            marginLeft: 6,
            marginRight: 6,
            marginTop: 4,
            marginBottom: 4,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: c.chipRadius,
            fontWeight: 800,
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: { borderColor: "rgba(0,0,0,0.06)" },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: { fontWeight: 800 },
        },
      },
    },
  };
}

// 既存3ムード（sei/wa/dou）
function buildMoodTokens(c) {
  return {
    sei: {
      palette: { mode: "light", primary: { main: c.seiPrimary }, background: { default: c.seiBg, paper: "#ffffff" }, text: { primary: "rgba(17,24,39,0.92)" } },
    },
    wa: {
      palette: { mode: "light", primary: { main: c.waPrimary }, background: { default: c.waBg, paper: "#ffffff" }, text: { primary: "rgba(17,24,39,0.92)" } },
    },
    dou: {
      palette: { mode: "light", primary: { main: c.douPrimary }, background: { default: c.douBg, paper: "#ffffff" }, text: { primary: "rgba(17,24,39,0.92)" } },
    },
  };
}

// 今日の気分10ムード（和色パレット）
// headingColor = h1〜h6 に適用する深い色調
const DAILY_MOOD_TOKENS = {
  kiy:  { headingColor: "#1a4a48", palette: { mode: "light", primary: { main: "#3a8a88", light: "#78bfbb" }, background: { default: "#f0f7f6", paper: "#ffffff" }, text: { primary: "rgba(17,24,39,0.92)" } } },
  shi:  { headingColor: "#1e3818", palette: { mode: "light", primary: { main: "#4a6e38", light: "#8aaa68" }, background: { default: "#eff4e8", paper: "#ffffff" }, text: { primary: "rgba(17,24,39,0.92)" } } },
  net:  { headingColor: "#580e05", palette: { mode: "light", primary: { main: "#a03828", light: "#d07058" }, background: { default: "#f8eeea", paper: "#ffffff" }, text: { primary: "rgba(17,24,39,0.92)" } } },
  may:  { headingColor: "#2e1050", palette: { mode: "light", primary: { main: "#6a4890", light: "#a888c8" }, background: { default: "#f3eef8", paper: "#ffffff" }, text: { primary: "rgba(17,24,39,0.92)" } } },
  tsu:  { headingColor: "#302820", palette: { mode: "light", primary: { main: "#6a6058", light: "#a8a098" }, background: { default: "#f2eeea", paper: "#ffffff" }, text: { primary: "rgba(17,24,39,0.92)" } } },
  kan:  { headingColor: "#4a3008", palette: { mode: "light", primary: { main: "#8a6820", light: "#d0a848" }, background: { default: "#faf4e0", paper: "#ffffff" }, text: { primary: "rgba(17,24,39,0.92)" } } },
  kana: { headingColor: "#050d40", palette: { mode: "light", primary: { main: "#0c2880", light: "#3870d0" }, background: { default: "#eaf2fc", paper: "#ffffff" }, text: { primary: "rgba(17,24,39,0.92)" } } },
  ase:  { headingColor: "#082840", palette: { mode: "light", primary: { main: "#105888", light: "#4898c8" }, background: { default: "#e8f4fb", paper: "#ffffff" }, text: { primary: "rgba(17,24,39,0.92)" } } },
  yor:  { headingColor: "#481020", palette: { mode: "light", primary: { main: "#883048", light: "#c87888" }, background: { default: "#f8eef0", paper: "#ffffff" }, text: { primary: "rgba(17,24,39,0.92)" } } },
  oso:  { headingColor: "#0c0c18", palette: { mode: "light", primary: { main: "#2a2a38", light: "#5a5a78" }, background: { default: "#ebebf2", paper: "#ffffff" }, text: { primary: "rgba(17,24,39,0.92)" } } },
};

function buildHeadingOverrides(headingColor, base) {
  if (!headingColor) return base.components;
  const h = { color: headingColor };
  return {
    ...base.components,
    MuiTypography: {
      styleOverrides: {
        h1: h, h2: h, h3: h, h4: h, h5: h, h6: h,
      },
    },
  };
}

export function createAppTheme(moodKey, themeConfig) {
  const c = themeConfig || THEME_DEFAULTS;
  const base = buildBase(c);
  const tokens = DAILY_MOOD_TOKENS[moodKey] || buildMoodTokens(c)[moodKey] || buildMoodTokens(c).sei;
  const components = buildHeadingOverrides(tokens.headingColor, base);
  return createTheme({ ...base, components, palette: tokens.palette || tokens });
}
